import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import Button from '../common/Button.js';
import qrScanNoticeService from '../../services/qrScanNoticeService.js';

const POLL_MS = 45000;

const QrScanNoticeControl = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const snoozeUntilRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const res = await qrScanNoticeService.unreadCount();
      const next = Number(res?.unread_count || 0);
      setCount(next);
      if (next > 0 && Date.now() >= snoozeUntilRef.current) {
        const list = await qrScanNoticeService.list(false);
        setItems(list?.data || []);
        setOpen(true);
      } else if (next === 0) {
        setItems([]);
        setOpen(false);
        snoozeUntilRef.current = 0;
      }
    } catch {
      setCount(0);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const checkOne = async (id) => {
    await qrScanNoticeService.check(id);
    await refresh();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => navigate('/qr-scan-notices')}
        className={`relative inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 ${
          count > 0 ? 'animate-pulse text-orange-600 border-orange-300' : ''
        }`}
        title="Scan notices"
        aria-label={count > 0 ? `${count} unchecked scan notices` : 'Scan notices'}
      >
        <Bell className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-semibold leading-[18px] text-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && items.length > 0 && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Shipment scanned at another branch</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Check each notice to stop this popup. The shipment status was not changed.
              </p>
            </div>
            <div className="overflow-y-auto p-4 space-y-3">
              {items.map((item) => (
                <div key={item.id} className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm">
                  <p className="text-gray-800">{item.message}</p>
                  <div className="mt-2 flex justify-end">
                    <Button variant="primary" onClick={() => checkOne(item.id)}>
                      Check
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  snoozeUntilRef.current = Date.now() + POLL_MS;
                  setOpen(false);
                }}
              >
                Later
              </Button>
              <Button variant="primary" onClick={() => navigate('/qr-scan-notices')}>
                Open list
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QrScanNoticeControl;
